// The top of Home: Zanetto Tools' dashboard (round 45).
//
// Nathan: "At Home I want it to be more like Zanetto Tools ... a heading that
// says Good evening, Username. Below that the date. Then ... the quotes
// summary with the cards ... exactly like Zanetto Tools. Followed by the
// quotes cards are the jobs cards. ... Below the job cards, have
// Announcements, just like Zanetto Tools, including the view all button and
// preview. Then continue down the page identical to zanetto tools."
//
// So this is ZT's Dashboard.jsx, block for block: the greeting and date, the
// Quotes and Jobs stat cards (number, label, sub-line; admins only, as ZT
// shows them), the Announcements card with View all and five previews, then
// the Recent Quotes and Active Jobs cards. Dayboard's own sections (re-
// planning, reminders, today, timesheets, getting started) carry on below it
// on HomePage.
//
// Where Dayboard's rules differ from ZT's pixels, the rule wins and says so:
//   * "Won this financial year", not ZT's calendar year (round 43's call,
//     kept on Nathan's word for round 45);
//   * Needs invoicing is amber, not ZT's orange: orange is Dayboard's brand
//     and an orange figure reads as a selected state;
//   * links are the brand's link colour, not ZT's blue (blue is information);
//   * dates read DD/MM/YYYY, and no em dashes.
import { Link } from 'react-router-dom';
import type { ReactNode } from 'react';
import { StatusBadge } from '../ui/StatusBadge';
import { formatCents } from '../../quotesApi';
import { longDayHeading, todayISO } from '../../lib/dates';
import type { HomeData } from '../../homeApi';

/** "morning" before noon, "afternoon" before five, else "evening" (ZT's buckets). */
export function greetingFor(hour: number): 'morning' | 'afternoon' | 'evening' {
  if (hour < 12) return 'morning';
  if (hour < 17) return 'afternoon';
  return 'evening';
}

/** The person's first name: "Nathan Zanetto" -> "Nathan". */
export function firstName(name: string): string {
  return name.trim().split(/\s+/)[0] ?? name;
}

/** Whole dollars, ZT's `fmt` for the large card figures. */
function wholeDollars(cents: number): string {
  return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 0 }).format(
    Math.round(cents / 100)
  );
}

/** An instant's local date, DD/MM/YYYY. */
function shortDay(iso: string): string {
  return new Date(iso).toLocaleDateString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

const COLOURS = {
  blue: 'text-blue-600',
  gray: 'text-gray-600',
  green: 'text-green-600',
  purple: 'text-purple-600',
  amber: 'text-amber-700',
} as const;

/** ZT's StatCard: the number first, then the label, then a sub-line. */
export function StatCard({
  label,
  value,
  sub,
  color,
  to,
  isLarge = false,
}: {
  label: string;
  value: ReactNode;
  sub: ReactNode;
  color: keyof typeof COLOURS;
  to: string;
  isLarge?: boolean;
}) {
  return (
    <Link
      to={to}
      className={`card block cursor-pointer px-5 py-4 transition-shadow hover:border-gray-300 hover:shadow-md ${
        isLarge ? 'col-span-2 lg:col-span-1' : ''
      }`}
    >
      <div className={`font-bold tabular-nums ${COLOURS[color]} ${isLarge ? 'text-xl' : 'text-2xl'}`}>{value}</div>
      <div className="mt-0.5 text-sm font-medium text-gray-900">{label}</div>
      <div className="mt-0.5 text-xs text-gray-600">{sub}</div>
    </Link>
  );
}

function SectionHeading({ children }: { children: ReactNode }) {
  return <h2 className="!mb-3 text-sm font-semibold uppercase tracking-wider text-gray-700">{children}</h2>;
}

export function HomeDashboard({
  data,
  userName,
  isAdmin,
  now = new Date(),
}: {
  data: HomeData | null;
  userName: string;
  isAdmin: boolean;
  /** The browser's clock: the person's own time of day decides the greeting. */
  now?: Date;
}) {
  const quotes = data?.quotes;
  const jobs = data?.jobs;

  return (
    <div className="mb-6">
      <div className="mb-6">
        <h1 className="!mb-0 text-2xl font-bold text-gray-900">
          Good {greetingFor(now.getHours())}, {firstName(userName)}
        </h1>
        <p className="!mb-0 mt-1 text-sm text-gray-600">{longDayHeading(todayISO())}</p>
      </div>

      {isAdmin && quotes && (
        <section aria-label="Quotes">
          <SectionHeading>Quotes</SectionHeading>
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            <StatCard label="Awaiting response" value={quotes.sent} sub="quotes sent" color="blue" to="/app/quotes?status=sent" />
            <StatCard label="Draft quotes" value={quotes.draft} sub="not yet sent" color="gray" to="/app/quotes?status=draft" />
            <StatCard
              label="Won this financial year"
              value={quotes.wonThisFinancialYear}
              sub={`${wholeDollars(quotes.wonThisFinancialYearCents)} (Incl. GST)`}
              color="green"
              to="/app/quotes?status=won"
            />
            <StatCard label="Total clients" value={data?.activeClients ?? 0} sub="active" color="purple" to="/app/clients" />
          </div>
        </section>
      )}

      {isAdmin && jobs && (
        <section aria-label="Jobs" className="mb-8 mt-6">
          <SectionHeading>Jobs</SectionHeading>
          <div className="grid grid-flow-row-dense grid-cols-2 gap-4 lg:grid-cols-4">
            <StatCard label="In progress" value={jobs.inProgress} sub="currently running" color="blue" to="/app/jobs?status=in_progress" />
            <StatCard
              label="Not yet started (Incl. GST)"
              value={wholeDollars(jobs.notStartedCents)}
              sub={`${jobs.notStarted} job${jobs.notStarted === 1 ? '' : 's'}`}
              color="gray"
              to="/app/jobs?status=pending"
              isLarge
            />
            <StatCard label="Needs invoicing" value={jobs.needsInvoicing} sub="marked complete" color="amber" to="/app/jobs?status=completed" />
            <StatCard
              label="Work in progress value (Incl. GST)"
              value={formatCents(jobs.workInProgressCents)}
              sub="active jobs, less invoiced"
              color="green"
              to="/app/jobs"
              isLarge
            />
          </div>
        </section>
      )}

      {data && data.recent.posts.length > 0 && (
        <div className="card mb-6">
          <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
            <h2 className="!mb-0 text-sm font-semibold text-gray-900">Announcements</h2>
            <Link to="/app/announcements" className="table-link text-xs">
              View all
            </Link>
          </div>
          <ul className="m-0 list-none divide-y divide-gray-100 p-0">
            {data.recent.posts.map((post) => (
              <li key={post.id}>
                <Link to={`/app/announcements/${post.id}`} className="block px-5 py-3 hover:bg-gray-50">
                  <div className="flex items-baseline justify-between gap-3">
                    <span className="truncate text-sm font-medium text-gray-900">
                      {post.body.split('\n')[0]}
                    </span>
                    <span className="flex-none text-xs text-gray-500">{shortDay(post.createdAt)}</span>
                  </div>
                  {post.body.includes('\n') && (
                    <p className="!mb-0 mt-0.5 line-clamp-2 text-sm text-gray-600">
                      {post.body.split('\n').slice(1).join(' ').trim()}
                    </p>
                  )}
                  <div className="mt-1 flex flex-wrap items-center gap-x-2 text-xs text-gray-500">
                    <span>{post.authorName ?? 'Someone since removed'}</span>
                    {post.edited && <em>· edited</em>}
                    {post.photos > 0 && (
                      <span className="inline-flex items-center gap-1">
                        <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                          <path strokeLinecap="round" strokeLinejoin="round" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        {post.photos}
                        <span className="sr-only"> photo{post.photos === 1 ? '' : 's'}</span>
                      </span>
                    )}
                    {post.documents > 0 && (
                      <span className="inline-flex items-center gap-1">
                        <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        {post.documents}
                        <span className="sr-only"> document{post.documents === 1 ? '' : 's'}</span>
                      </span>
                    )}
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}

      {!isAdmin && data && (
        <p className="!mb-3 text-xs text-gray-500">Dollar figures include GST, except totals from GST-exempt quotes.</p>
      )}

      {data && (
        <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
          <RecentCard
            title="Recent Quotes"
            viewAll="/app/quotes?status=all"
            empty="No quotes yet."
            headings={['Quote #', 'Client', 'Status', 'Total']}
            rows={(quotes?.recent ?? []).map((q) => ({
              id: q.id,
              to: `/app/quotes/${q.id}`,
              number: q.number,
              client: q.clientName,
              status: <StatusBadge status={q.status} kind="quote" />,
              amount: formatCents(q.totalCents),
            }))}
          />
          <RecentCard
            title="Active Jobs"
            viewAll="/app/jobs"
            empty="No active jobs."
            headings={['Job #', 'Client', 'Status', 'Value']}
            rows={(data.activeJobs ?? []).map((j) => ({
              id: j.id,
              to: `/app/jobs/${j.id}`,
              number: j.number,
              client: j.clientName ?? 'Internal work',
              status: <StatusBadge status={j.status} kind="job" />,
              amount: formatCents(j.valueCents),
            }))}
          />
        </div>
      )}
    </div>
  );
}

interface RecentRow {
  id: string;
  to: string;
  number: string;
  client: string;
  status: ReactNode;
  amount: string;
}

/** ZT's Recent Quotes / Active Jobs card: stacked links on a phone, a table from sm. */
function RecentCard({
  title,
  viewAll,
  empty,
  headings,
  rows,
}: {
  title: string;
  viewAll: string;
  empty: string;
  headings: [string, string, string, string];
  rows: RecentRow[];
}) {
  return (
    <div className="card">
      <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
        <h2 className="!mb-0 text-sm font-semibold text-gray-900">{title}</h2>
        <Link to={viewAll} className="table-link text-xs">
          View all
        </Link>
      </div>
      {rows.length === 0 ? (
        <p className="!mb-0 px-5 py-6 text-center text-sm text-gray-500">{empty}</p>
      ) : (
        <>
          <ul className="m-0 list-none divide-y divide-gray-100 p-0 sm:hidden">
            {rows.map((row) => (
              <li key={row.id}>
                <Link to={row.to} className="flex min-h-[44px] items-center justify-between gap-3 px-5 py-2 hover:bg-gray-50">
                  <span className="min-w-0">
                    <span className="font-mono text-xs font-medium text-brand-800">{row.number}</span>
                    <span className="block truncate text-sm text-gray-700">{row.client}</span>
                  </span>
                  <span className="flex flex-none flex-col items-end gap-1">
                    <span className="text-sm tabular-nums text-gray-900">{row.amount}</span>
                    {row.status}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
          <div className="hidden overflow-x-auto sm:block">
            <table className="data-table min-w-[440px]">
              <thead>
                <tr>
                  <th>{headings[0]}</th>
                  <th>{headings[1]}</th>
                  <th>{headings[2]}</th>
                  <th className="text-right">{headings[3]}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id}>
                    <td>
                      <Link to={row.to} className="table-link font-mono text-xs">
                        {row.number}
                      </Link>
                    </td>
                    <td className="table-muted">{row.client}</td>
                    <td>{row.status}</td>
                    <td className="text-right tabular-nums">{row.amount}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
